import React, { useEffect, useMemo } from 'react'
import { CurrentTestState, CurrentUserAssessmentState } from '@app/storage'
import { Box, Typography, styled, Chip } from '@mui/material'
import { useSnapshot } from 'valtio'
import { NUMBER_OF_QUESTIONS_ATTRIBUTES } from '@app/constants'
import QuestionToolsMenu from './QuestionToolsMenu'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPennant } from '@fortawesome/pro-solid-svg-icons/faPennant'
import { useContext } from 'react'
import {
  QuestionContextMenuContext,
  MARK_FOR_REVIEW_STATUS_ID,
} from '@app/components/common/QuestionContextMenuContext'
import { ScreenReaderInfo } from '@app/components/common/ScreenReaderInfo'
import { announce } from '@app/components/common/ScreenReaderAnnouncer'
import { isSingleQuestionWritingTest } from '@app/helpers/isSingleQuestionWritingTest'

type Props = {
  reviewMode?: boolean
  hasHighlights?: boolean
  checkHighlightedElement?: () => boolean
  onHighlightSelection?: () => void
  onResetHighlight?: () => void
  onRemoveHighlight?: () => void
}

const InteractionLayoutHeader: React.FC<Props> = ({
  reviewMode = false,
  hasHighlights = false,
  checkHighlightedElement,
  onHighlightSelection,
  onResetHighlight,
  onRemoveHighlight,
}) => {
  const { currentItemResponse, itemsResponses } = useSnapshot(CurrentTestState)
  const { program, subject } = useSnapshot(CurrentUserAssessmentState)
  const currentItemIndex = itemsResponses.findIndex(item => item.id === currentItemResponse.id)

  const { markedItems, setMarkForReview, pendingStatusAnnouncementId } = useContext(QuestionContextMenuContext)

  const currentQuestionNumber = currentItemIndex + 1

  const isQuestionMarkedForReview = useMemo(
    () => markedItems.includes(currentItemResponse.itemId),
    [markedItems, currentItemResponse.itemId],
  )

  const hideMarkForReview = useMemo(
    () => isSingleQuestionWritingTest({ program, subject, itemCount: itemsResponses.length }),
    [program, subject, itemsResponses.length],
  )

  // a11y: announce the mark/unmark toggle via the global announcer
  // skip in review (no mark tool).
  useEffect(() => {
    if (reviewMode || pendingStatusAnnouncementId !== MARK_FOR_REVIEW_STATUS_ID) return
    announce(
      `Question ${currentQuestionNumber} ${isQuestionMarkedForReview ? 'marked' : 'unmarked'} for review`,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingStatusAnnouncementId])

  // a11y: describedby target only (no aria-live, so it can't double the global
  // announce()); the button reads it on focus after the tools menu closes.
  const reviewStatusAnnouncement =
    !reviewMode && pendingStatusAnnouncementId === MARK_FOR_REVIEW_STATUS_ID
      ? `Question ${currentQuestionNumber} ${isQuestionMarkedForReview ? 'marked' : 'unmarked'} for review`
      : ''

  return (
    <HeaderWrapper tabIndex={-1} reviewMode={reviewMode} toolsHidden={hideMarkForReview && !hasHighlights}>
      <ScreenReaderInfo id={MARK_FOR_REVIEW_STATUS_ID}>
        {reviewStatusAnnouncement}
      </ScreenReaderInfo>

      <HeaderTitle
        {...NUMBER_OF_QUESTIONS_ATTRIBUTES}
        component='h2'
        variant='h5'
        tabIndex={-1}
        aria-label={`Question ${currentQuestionNumber} of ${itemsResponses.length}`}
      >
        Question {currentQuestionNumber} of {itemsResponses.length}
      </HeaderTitle>

      {/* Marks persist in localStorage, so suppress a stale chip the student can no longer clear. */}
      {!reviewMode && !hideMarkForReview && isQuestionMarkedForReview && (
        <Box ml={2}>
          <StyledReviewChip size='small' icon={<FontAwesomeIcon icon={faPennant} />} label='Review' />
        </Box>
      )}

      {!reviewMode && (
        <Box ml='auto'>
          <QuestionToolsMenu
            hasHighlights={hasHighlights}
            checkHighlightedElement={checkHighlightedElement}
            onHighlightSelection={onHighlightSelection}
            onResetHighlight={onResetHighlight}
            onRemoveHighlight={onRemoveHighlight}
            onMarkForReview={setMarkForReview}
            markedItems={markedItems}
            hideMarkForReview={hideMarkForReview}
          />
        </Box>
      )}
    </HeaderWrapper>
  )
}

const HeaderWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'reviewMode' && prop !== 'toolsHidden',
})<{ reviewMode?: boolean; toolsHidden?: boolean }>(
  ({ theme, reviewMode = false, toolsHidden = false }) => {
    // The tools menu is what gives this bar its height: a small IconButton is
    // 28px against a 16px title. Review mode already pads more for exactly that
    // reason (it has no tools menu), so reuse its value when the menu is hidden
    // for a single-question Writing test.
    //
    // Only test-taking needs the mobile compensation. toolsHidden is also true
    // when reviewing such a test, and every other review screen sits at the
    // uncompensated mobile height — padding those would make this one review
    // screen taller than its siblings.
    const compensateForMissingTools = !reviewMode && toolsHidden

    return {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      padding: theme.spacing(reviewMode || toolsHidden ? 4.5 : 3, 4),
      borderBottom: `1px solid ${theme.palette.grey[100]}`,

      [theme.breakpoints.down('mobile')]: {
        // Mobile padding is tighter, so it needs its own value:
        // 14 + 16 title + 14 + 1 border == 8 + 28 button + 8 + 1.
        padding: theme.spacing(compensateForMissingTools ? 3.5 : 2, 3),
      },
    }
  },
)

const HeaderTitle = styled(Typography)(
  ({ theme }) => `
    color: ${theme.palette.grey[800]};
`,
) as typeof Typography

const StyledReviewChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.purple[50],
  color: theme.palette.purple['A700'],
  padding: theme.spacing(1, 2),

  '& .MuiChip-icon': {
    fontSize: '0.6875rem',
    color: theme.palette.purple['A700'],
  },
}))

export default InteractionLayoutHeader
